<template>
  <div>
    <SoPageHead :title="$t('securone.nav.categories')">
      <template #subtitle>{{ $tc("securone.categories.count", categories.length, { n: categories.length }) }}</template>
      <select v-if="showDepartmentFilter" v-model="departmentId" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.department.filter_all") }}</option>
        <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
      </select>
      <SoSearch v-model="search" :placeholder="$t('securone.categories.search')" />
      <router-link v-if="canWrite.category" :to="{ name: 'securone_category_new' }" class="sd-btn sd-btn--success">{{ $t("securone.categories.new") }}<BaseIcon icon="plus" class="ml-2" /></router-link>
    </SoPageHead>

    <SoNote v-if="!canWrite.category" tone="info" class="mb-4">{{ $t("securone.categories.readonly_note") }}</SoNote>

    <div class="sd-card overflow-hidden mb-4">
      <div class="overflow-x-auto">
        <table class="sd-table sd-table--compact w-full text-sm">
          <thead>
            <tr>
              <th class="text-left">{{ $t("securone.categories.category") }}</th>
              <th class="text-left">{{ $t("securone.categories.scope") }}</th>
              <th class="text-left">{{ $t("securone.common.groups") }}</th>
              <th class="text-left">{{ $t("securone.common.reach") }}</th>
              <th class="text-left"></th>
              <th class="text-left">{{ $t("securone.common.updated") }}</th>
              <th v-if="anyEditable"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in filtered" :key="c._id" class="border-b border-secondary-100 last:border-0" :class="{ 'cursor-pointer': c.canEdit, 'opacity-60': c.isActive === false }" @click="c.canEdit && edit(c)">
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded bg-secondary-100 flex items-center justify-center text-secondary-600 flex-shrink-0"><BaseIcon :icon="categoryIcon(c.icon)" /></div>
                  <div class="min-w-0">
                    <div class="font-semibold text-gray-800">{{ c.name }}<SoChip v-if="c.hasActiveEvent" tone="live" class="ml-2">{{ $t("securone.categories.broadcast_running") }}</SoChip><SoChip v-if="c.isActive === false" class="ml-2">{{ $t("securone.categories.inactive") }}</SoChip></div>
                    <div class="text-xs text-gray-500 mt-0.5 truncate" style="max-width: 36ch">{{ c.instruction }}</div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ scopeLabel(c) }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <SoChip v-for="g in c.groupsDetail" :key="g._id">{{ g.name }} <span class="ml-1 font-mono text-gray-400">{{ g.memberCount }}</span></SoChip>
                  <span v-if="!c.groupsDetail.length" class="text-xs text-red-600">{{ $t("securone.categories.no_groups") }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{{ reachLine(c.reach) }}</td>
              <td class="px-4 py-3">
                <!-- AR1 for a principal; a Securdia account is never a recipient (AR-S1) -->
                <SoChip v-if="isPrincipal" :tone="c.canSend ? 'role' : 'default'">{{ c.canSend ? $t("securone.categories.you_can_send") : $t("securone.categories.not_yours") }}</SoChip>
                <SoChip v-else tone="plain">{{ $t("securone.categories.not_recipient_securdia") }}</SoChip>
              </td>
              <td class="px-4 py-3 text-xs text-gray-500 font-mono">{{ dateStamp(c.updatedAt) }}</td>
              <td v-if="anyEditable" class="px-4 py-3 text-right"><button v-if="c.canEdit" class="sd-btn sd-btn--secondary sd-btn--sm" @click.stop="edit(c)">{{ $t("edit") }}<BaseIcon icon="pencil-alt" class="ml-1" /></button></td>
            </tr>
            <tr v-if="!filtered.length"><td colspan="7" class="px-4 py-8 text-center text-gray-400">{{ $t("securone.categories.none") }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <SoNote tone="muted">{{ $t("securone.categories.access_rule") }}</SoNote>
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoSearch from "./components/SoSearch.vue";
import SoChip from "./components/SoChip.vue";
import SoNote from "./components/SoNote.vue";
import api from "./securone.api";
import { categoryIcon, dateStamp, scopeLabel } from "./securone.helpers";

// D-09. The admin list is a scoped query on the server (spec §4); the edit
// affordance renders only for rows the actor may write (canEdit from the
// server, never a role string).
export default {
  name: "SecurOneCategories",
  components: { SoPageHead, SoSearch, SoChip, SoNote },
  data() {
    return { categories: [], search: "", departmentId: this.$store.getters["securone/departmentFilter"] || "" };
  },
  computed: {
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    isPrincipal() {
      return this.$store.getters["securone/isPrincipal"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    showDepartmentFilter() {
      return this.$store.getters["securone/showDepartmentFilter"];
    },
    adminVersion() {
      return this.$store.state.securone.adminVersion;
    },
    anyEditable() {
      return this.categories.some((c) => c.canEdit);
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.categories;
      return this.categories.filter((c) => [c.name, c.instruction, ...c.departmentNames, ...c.groupsDetail.map((g) => g.name)].join(" ").toLowerCase().includes(q));
    },
  },
  methods: {
    categoryIcon,
    dateStamp,
    scopeLabel(c) {
      return scopeLabel(this, c.scope, c.departmentNames);
    },
    reachLine(reach) {
      let line = this.$tc("securone.common.people_in_groups", reach.groups, { people: reach.people, groups: reach.groups });
      if (reach.sms) line += " · " + this.$tc("securone.common.sms_contacts_count", reach.sms, { n: reach.sms });
      return line;
    },
    async load() {
      try {
        this.categories = await api.categories({ departmentId: this.departmentId || undefined });
      } catch (error) {
        this.handleError(error);
      }
    },
    edit(c) {
      this.$router.push({ name: "securone_category_edit", params: { id: c._id } });
    },
  },
  watch: {
    adminVersion() {
      this.load();
    },
    departmentId(value) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", value || null);
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.categories"), "securone_categories");
    this.load();
  },
};
</script>
